'use client';

import {
  type IOrderVerifyRequestOrderItem,
  type ISalePageDetailResponse,
  type ISalePageDetailResponseClassProductResponse,
  type ISalePageProductAdditionalInformResponse,
} from '@/api/types';
import { Button, getRouterPath } from '@/components';
import { useIsLoggedIn } from '@/entities';
import { useUpsertOrderEntry } from '@/entities/payment';
import { useProductAdditionalInforms } from '@/entities/salePage';
import { useDeviceSize } from '@/hooks/useDeviceSize';
import { useLoginInducingModal } from '@/hooks/useLoginInducingModal';
import { pushDataLayer } from '@/lib/gtmDataLayer';
import { IMoneyResponse } from '@/types/common';
import { Column, Element, Icon, Row, useOverlay } from '@vuddy/components';
import { theme } from '@vuddy/components/theme';
import { trackEvent } from '@vuddy/utils';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  ClassPurchaseBottomSheet,
  ClassPurchaseDialog,
  type ClassVoucher,
  type ClassVoucherSelectionMode,
} from '../class';
import * as toastStyles from './cartToast.css';
import * as styles from './classPurchase.css';
import * as dropdownStyles from './dropdown.css';

type ClassPurchaseSalePage = Pick<
  ISalePageDetailResponse,
  'id' | 'name' | 'channelId' | 'channel' | 'type' | 'clazz'
>;

interface ClassPurchaseProps {
  salePage: ClassPurchaseSalePage;
  forceFetchAdditionalInforms?: boolean;
  selectionMode?: ClassVoucherSelectionMode;
}

const formatPriceLabel = (price: number) => `${price.toLocaleString()}원`;

const formatMoney = (money?: IMoneyResponse) =>
  typeof money?.amount === 'number'
    ? `${money.amount.toLocaleString()}원`
    : undefined;

const resolveProductPrice = (
  product: ISalePageDetailResponseClassProductResponse
) => product.discountPrice?.amount ?? product.normalPrice?.amount ?? 0;

export const ClassPurchase = ({
  salePage,
  forceFetchAdditionalInforms = false,
  selectionMode = 'single',
}: ClassPurchaseProps) => {
  const device = useDeviceSize();
  const { openBottomSheet, openModal } = useOverlay();
  const { data: additionalInforms } = useProductAdditionalInforms(salePage.id, {
    enabled: !!forceFetchAdditionalInforms,
  });
  const { isLoggedIn } = useIsLoggedIn();
  const loginConfirm = useLoginInducingModal();
  const router = useRouter();
  const routerPath = getRouterPath();
  const { mutateAsync: upsertOrderEntry, isPending: isUpsertingOrderEntry } =
    useUpsertOrderEntry();
  const { channelId } = salePage;

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [verifiedVoucherCodes, setVerifiedVoucherCodes] = useState<
    Map<string, string>
  >(new Map());
  const classProductList = salePage.clazz?.classProducts;
  const classProducts = useMemo(
    () => classProductList ?? [],
    [classProductList]
  );

  const vouchers = useMemo<ClassVoucher[]>(() => {
    return classProducts.map(product => {
      // discountLabel에 값이 있을 때만 할인 뱃지 표시
      const discountBadge = product.discountLabel || undefined;
      return {
        id: String(product.id),
        name: product.name,
        description: product.description,
        price: resolveProductPrice(product),
        durationLabel: product.passDurationDays,
        highlightLabel: product.bestLabelEnabled ? 'BEST' : undefined,
        normalPriceLabel: product.normalPrice?.amount
          ? `정상가 ${formatMoney(product.normalPrice)}`
          : undefined,
        discountPriceLabel: product.discountPrice?.amount
          ? `${product.normalPrice?.amount === 0 ? '판매가' : '할인가'} ${formatMoney(product.discountPrice)}`
          : undefined,
        discountBadgeLabel: discountBadge ?? undefined,
        priceLabel: product.installmentPrice,
        subLabel: product.discountPrice?.amount ? '12개월 할부가' : undefined,
        requiresPromotionCode: product.requiresPromotionCode,
      } satisfies ClassVoucher;
    });
  }, [classProducts]);

  const unavailableBySaleStatus = useMemo(() => {
    return classProducts
      .filter(product => product.saleStatus !== 'ON_SALE')
      .map(product => String(product.id));
  }, [classProducts]);

  const disabledVoucherIds = useMemo(() => {
    const disabled = new Set<string>(unavailableBySaleStatus);

    additionalInforms?.forEach(info => {
      if (!info.isPurchasable || info.remainingQuantity <= 0) {
        disabled.add(String(info.productId));
      }
    });

    return Array.from(disabled);
  }, [additionalInforms, unavailableBySaleStatus]);

  const disabledVoucherSet = useMemo(
    () => new Set(disabledVoucherIds),
    [disabledVoucherIds]
  );

  const selectedVouchers = useMemo(() => {
    const voucherMap = new Map(vouchers.map(voucher => [voucher.id, voucher]));
    return selectedIds
      .map(id => voucherMap.get(id))
      .filter((voucher): voucher is ClassVoucher => Boolean(voucher));
  }, [selectedIds, vouchers]);

  const availableVoucherCount = useMemo(() => {
    return vouchers.filter(voucher => !disabledVoucherSet.has(voucher.id))
      .length;
  }, [disabledVoucherSet, vouchers]);

  const additionalInformMap = useMemo(() => {
    if (!additionalInforms)
      return new Map<string, ISalePageProductAdditionalInformResponse>();
    return new Map(
      additionalInforms.map(info => [String(info.productId), info])
    );
  }, [additionalInforms]);

  useEffect(() => {
    if (disabledVoucherIds.length === 0) return;
    setSelectedIds(prev => prev.filter(id => !disabledVoucherIds.includes(id)));
  }, [disabledVoucherIds]);

  useEffect(() => {
    if (selectionMode !== 'single') return;
    setSelectedIds(prev => {
      if (prev.length <= 1) {
        return prev;
      }
      return prev.slice(0, 1);
    });
  }, [selectionMode]);

  const selectedProducts = useMemo(() => {
    return classProducts.filter(product =>
      selectedIds.includes(String(product.id))
    );
  }, [classProducts, selectedIds]);

  const totalPrice = useMemo(() => {
    return selectedProducts.reduce(
      (sum, product) => sum + resolveProductPrice(product),
      0
    );
  }, [selectedProducts]);

  const selectedLabel = useMemo(() => {
    if (selectedVouchers.length === 0) return '이용권을 선택해주세요';
    if (selectedVouchers.length === 1) {
      return selectedVouchers[0].name;
    }
    return `선택한 이용권 ${selectedVouchers.length}개`;
  }, [selectedVouchers]);

  const isSelectorDisabled =
    vouchers.length === 0 || availableVoucherCount === 0;

  const handleSelectionChange = useCallback((ids: string[]) => {
    setSelectedIds(ids);
  }, []);

  const hasUnavailableProducts = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return false;
      if (!additionalInforms) return false;
      return ids.some(id => {
        const info = additionalInformMap.get(id);
        if (!info) return true;
        return !info.isPurchasable || info.remainingQuantity <= 0;
      });
    },
    [additionalInformMap, additionalInforms]
  );

  const runPurchaseFlow = useCallback(
    async (ids: string[], promotionCodeMap = verifiedVoucherCodes) => {
      if (isUpsertingOrderEntry) {
        return;
      }

      trackEvent('shop', 'click_sale_buy', {
        channel_id: salePage.channelId,
        sale_type: salePage.type,
      });

      if (ids.length === 0) {
        toast.error('먼저 이용권을 선택해주세요.');
        return;
      }

      const idSet = new Set(ids);
      const products = classProducts.filter(product =>
        idSet.has(String(product.id))
      );

      if (products.length === 0) {
        toast.error('선택한 이용권 정보를 확인할 수 없습니다.');
        return;
      }

      if (!isLoggedIn) {
        loginConfirm();
        return;
      }

      if (hasUnavailableProducts(ids)) {
        toast.error('선택한 이용권을 구매할 수 없습니다.');
        return;
      }

      if (!channelId) {
        toast.error('채널 정보를 확인할 수 없습니다.');
        return;
      }

      const hasUnverifiedProduct = products.some(product => {
        return (
          product.requiresPromotionCode &&
          !promotionCodeMap.has(String(product.id))
        );
      });

      if (hasUnverifiedProduct) {
        toast.info('코드를 입력해주세요.', {
          className: toastStyles.verificationCodeToast,
        });
        return;
      }

      const orderItems: IOrderVerifyRequestOrderItem[] = products.map(
        product => {
          const promotionCode = promotionCodeMap.get(String(product.id));

          return {
            referenceId: product.id,
            referenceType: product.type,
            quantity: 1,
            ...(promotionCode ? { promotionCode } : {}),
          };
        }
      );

      const totalValue = products.reduce(
        (sum, product) => sum + resolveProductPrice(product),
        0
      );
      pushDataLayer({
        event: 'begin_checkout',
        value: totalValue,
        currency: 'KRW',
        shipping: 0,
        items: products.map(product => ({
          item_id: String(salePage.id),
          item_name: salePage.name,
          item_type: 'class',
          item_category: salePage.type,
          item_variant: product.name,
          creator_id: String(salePage.channelId),
          creator_name: salePage.channel.name,
          price: resolveProductPrice(product),
          quantity: 1,
        })),
      });

      try {
        const { entryId } = await upsertOrderEntry({
          salePageId: salePage.id,
          entrySource: 'sale',
          channelId,
          orderItems,
        });

        router.push(routerPath.orderSheetByEntry(salePage.id, entryId));
      } catch {}
    },
    [
      channelId,
      classProducts,
      hasUnavailableProducts,
      isUpsertingOrderEntry,
      isLoggedIn,
      verifiedVoucherCodes,
      salePage,
      loginConfirm,
      router,
      routerPath,
      upsertOrderEntry,
    ]
  );

  const handleConfirmSelection = useCallback(
    (
      ids: string[],
      _selectedVouchers: ClassVoucher[],
      nextVerifiedVoucherCodes: Map<string, string>
    ) => {
      setSelectedIds(ids);
      setVerifiedVoucherCodes(new Map(nextVerifiedVoucherCodes));

      if (ids.length > 0) {
        void runPurchaseFlow(ids, nextVerifiedVoucherCodes);
      }
    },
    [runPurchaseFlow]
  );

  const handleOpenBottomSheet = useCallback(() => {
    if (vouchers.length === 0) return;

    // 이용권이 1개이고 선택된 게 없으면 자동 선택
    let initialIds = selectedIds;
    if (availableVoucherCount === 1 && selectedIds.length === 0) {
      const availableVoucher = vouchers.find(
        voucher => !disabledVoucherSet.has(voucher.id)
      );
      if (availableVoucher) {
        initialIds = [availableVoucher.id];
      }
    }

    openBottomSheet({
      component: ClassPurchaseBottomSheet,
      props: {
        vouchers,
        selectionMode,
        initialSelectedIds: initialIds,
        initialVerifiedVoucherCodes: verifiedVoucherCodes,
        disabledVoucherIds,
        requireSelection: true,
        onSelectionChange: handleSelectionChange,
        onConfirm: handleConfirmSelection,
      },
    });
  }, [
    availableVoucherCount,
    disabledVoucherIds,
    disabledVoucherSet,
    handleConfirmSelection,
    handleSelectionChange,
    openBottomSheet,
    selectedIds,
    selectionMode,
    verifiedVoucherCodes,
    vouchers,
  ]);

  const handleOpenDialog = useCallback(() => {
    if (vouchers.length === 0) return;

    // 이용권이 1개이고 선택된 게 없으면 자동 선택
    let initialIds = selectedIds;
    if (availableVoucherCount === 1 && selectedIds.length === 0) {
      const availableVoucher = vouchers.find(
        voucher => !disabledVoucherSet.has(voucher.id)
      );
      if (availableVoucher) {
        initialIds = [availableVoucher.id];
      }
    }

    openModal({
      component: ClassPurchaseDialog,
      props: {
        vouchers,
        selectionMode,
        initialSelectedIds: initialIds,
        initialVerifiedVoucherCodes: verifiedVoucherCodes,
        disabledVoucherIds,
        requireSelection: true,
        onSelectionChange: handleSelectionChange,
        onConfirm: handleConfirmSelection,
        style: {
          container: {
            borderRadius: theme.size.radius_2xl,
          },
        },
      },
    });
  }, [
    availableVoucherCount,
    disabledVoucherIds,
    disabledVoucherSet,
    handleConfirmSelection,
    handleSelectionChange,
    openModal,
    selectedIds,
    selectionMode,
    verifiedVoucherCodes,
    vouchers,
  ]);

  const handleSelectorClick = useCallback(() => {
    if (isSelectorDisabled) return;

    // 로그인 체크 - 바텀시트/모달 열기 전에 로그인 유도
    if (!isLoggedIn) {
      loginConfirm();
      return;
    }

    if (device === 'mobile') {
      handleOpenBottomSheet();
      return;
    }
    handleOpenDialog();
  }, [
    device,
    handleOpenBottomSheet,
    handleOpenDialog,
    isLoggedIn,
    isSelectorDisabled,
    loginConfirm,
  ]);

  const isMobile = device === 'mobile';
  const isDesktopLike = device !== 'mobile' && device !== '';

  const primaryButtonLabel = '이용권 구매하기';

  const isPrimaryButtonDisabled =
    isSelectorDisabled || (isDesktopLike && selectedProducts.length === 0);

  const handlePrimaryButtonClick = useCallback(() => {
    if (!isDesktopLike) {
      if (selectedIds.length === 0) {
        handleSelectorClick();
        return;
      }

      runPurchaseFlow(selectedIds, verifiedVoucherCodes);
      return;
    }

    runPurchaseFlow(selectedIds, verifiedVoucherCodes);
  }, [
    handleSelectorClick,
    isDesktopLike,
    runPurchaseFlow,
    selectedIds,
    verifiedVoucherCodes,
  ]);

  if (vouchers.length === 0) {
    return null;
  }

  return (
    <Column className={styles.container}>
      {!isMobile && (
        <Element
          as="button"
          type="button"
          className={clsx(dropdownStyles.trigger, styles.selectorButton)}
          onClick={handleSelectorClick}
          disabled={isSelectorDisabled}
          aria-haspopup="dialog"
          aria-label="이용권 선택"
          data-state="closed">
          <Row gap="gap_small" alignItems="center" flex={1}>
            <Element
              textAlign="left"
              flex={1}
              typography="bodyBase"
              color={selectedVouchers.length > 0 ? 'primary' : 'placeholder'}>
              {selectedLabel}
            </Element>
            <Icon
              name="FILL_TRIANGLE_DOWN"
              size="16px"
              color={selectedVouchers.length > 0 ? 'primary' : 'placeholder'}
            />
          </Row>
        </Element>
      )}
      {!isMobile && (
        <Row className={styles.summaryRow}>
          <Element typography="bodySmallAccent" color="secondary">
            총 금액
          </Element>
          <Element typography="titleSmall" color="primary">
            {formatPriceLabel(totalPrice)}
          </Element>
        </Row>
      )}
      <Button
        size="large"
        fullWidth
        onClick={handlePrimaryButtonClick}
        disabled={isPrimaryButtonDisabled}>
        {primaryButtonLabel}
      </Button>
    </Column>
  );
};
